const MAX_MESSAGE_LENGTH = 300
const HTML_MARKUP = /<[a-z!/][^>]*>/i

type ErrorResponse = {
  data?: unknown
  headers?: unknown
}

function getResponse(error: unknown): ErrorResponse | undefined {
  if (!error || typeof error !== 'object') return undefined

  const response = (error as { response?: unknown }).response
  if (!response || typeof response !== 'object') return undefined

  return response as ErrorResponse
}

function getContentType(response: ErrorResponse): string {
  const headers = response.headers
  if (!headers || typeof headers !== 'object') return ''

  const get = (headers as { get?: unknown }).get
  if (typeof get === 'function') {
    const value = get.call(headers, 'content-type')
    return typeof value === 'string' ? value : ''
  }

  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === 'content-type')
  return typeof entry?.[1] === 'string' ? entry[1] : ''
}

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item)
      if (found) return found
    }
  }
  return undefined
}

/** ProblemDetails, ValidationProblemDetails i plochý SerializableError z `BadRequest(ModelState)`. */
function getMessageFromObject(data: Record<string, unknown>): string | undefined {
  const errors = data.errors
  if (errors && typeof errors === 'object') {
    const found = firstString(Object.values(errors))
    if (found) return found
  }

  const detail = firstString(data.detail) ?? firstString(data.title)
  if (detail) return detail

  return firstString(Object.values(data))
}

/**
 * Text chyby určený uživateli. Tělo odpovědi se použije, jen když jde o krátkou textovou
 * hlášku ze serveru — SPA fallback vrací na API požadavek celé `index.html` jako string,
 * takže samotné `typeof data === 'string'` nestačí.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const response = getResponse(error)
  if (!response) return fallback

  const data = response.data

  if (typeof data === 'string') {
    const text = data.trim()
    const contentType = getContentType(response).toLowerCase()
    const isPlainText = !contentType || contentType.startsWith('text/plain')

    if (!text || text.length > MAX_MESSAGE_LENGTH || !isPlainText || HTML_MARKUP.test(text))
      return fallback

    return text
  }

  if (data && typeof data === 'object') {
    const message = getMessageFromObject(data as Record<string, unknown>)
    if (message && message.length <= MAX_MESSAGE_LENGTH) return message
  }

  return fallback
}
